import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["className", "id", "label", "caption", "children", "flow", "isDisabled"];
import React, { useMemo, useContext } from 'react';
import getConfig from '../../js/settings';
import { Flex, FlexItem, Label, Caption, Element } from '../';
import { appendHtmlClass, getAnimationDuration, getAnimationEasing, getSpaceValue } from '../../js/util';
import { FormValueContext } from './helper';
import './_index.scss';
var FIELD_MARGIN_BOTTOM = getConfig('FIELD_MARGIN_BOTTOM');
var FIELD_FLOW_HORIZONTAL = 'horizontal';
var FIELD_FLOW_VERTICAL = 'vertical';
var Field = /*#__PURE__*/React.memo(function (_ref) {
  var className = _ref.className,
    id = _ref.id,
    label = _ref.label,
    caption = _ref.caption,
    children = _ref.children,
    flow = _ref.flow,
    isDisabled = _ref.isDisabled,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useContext = useContext(FormValueContext),
    fields = _useContext.fields;
  var input = useMemo(function () {
    var original = React.Children.only(children);
    if (!id || original.props.hasOwnProperty(id) && typeof original.props.id === 'string' && original.props.id.length > 0) return original;
    return /*#__PURE__*/React.cloneElement(original, {
      id: id,
      isDisabled: isDisabled
    });
  }, [children, id, isDisabled]);
  var name = useMemo(function () {
    return input && input.props && input.props.hasOwnProperty('name') ? input.props.name : null;
  }, [input]);
  var field = useMemo(function () {
    return fields.hasOwnProperty(name) ? fields[name] : null;
  }, [name, fields]);
  var isTouched = field && field.isTouched;
  var showError = isTouched && !field.isValid;
  var error = field && showError && field.error;
  var dir = flow === FIELD_FLOW_HORIZONTAL ? 'row' : 'column';
  var newClassName = appendHtmlClass(className, "field " + flow);
  var captionAnimate = caption && !error ? 'visible' : 'hidden';
  var errorAnimate = error ? 'visible' : 'hidden';
  var animationProps = useMemo(function () {
    var hidden = {
      opacity: 0,
      y: getSpaceValue(2),
      transitionEnd: {
        position: 'absolute'
      }
    };
    var visible = {
      opacity: 1,
      y: 0,
      zIndex: 'auto',
      transitionEnd: {
        position: 'static'
      }
    };
    var transition = {
      duration: getAnimationDuration(getConfig('FIELD_TRANSITION_DURATION')),
      easing: getAnimationEasing(getConfig('FIELD_TRANSITION_EASING'))
    };
    return {
      isAnimated: true,
      variants: {
        hidden: hidden,
        visible: visible
      },
      transition: transition
    };
  }, []);
  return /*#__PURE__*/React.createElement(Flex, Object.assign({
    className: newClassName,
    flow: dir,
    alignItemsY: "stretch",
    gapX: FIELD_FLOW_HORIZONTAL ? 'margin' : 'none',
    marginBottom: FIELD_MARGIN_BOTTOM
  }, rest), label && /*#__PURE__*/React.createElement(FlexItem, {
    className: "label-wrapper",
    span: "shrink"
  }, /*#__PURE__*/React.createElement(Label, {
    htmlFor: id,
    hasError: showError,
    isDisabled: isDisabled
  }, label)), /*#__PURE__*/React.createElement(FlexItem, {
    className: "input-wrapper",
    span: flow === FIELD_FLOW_HORIZONTAL ? 'auto' : 12
  }, input, /*#__PURE__*/React.createElement(Element, {
    as: "div",
    className: "caption-wrapper"
  }, /*#__PURE__*/React.createElement(Caption, Object.assign({
    animate: captionAnimate
  }, animationProps), caption ? caption : ''), /*#__PURE__*/React.createElement(Caption, Object.assign({
    className: "error",
    color: "error",
    animate: errorAnimate
  }, animationProps), error ? field.error : ''))));
});
Field.defaultProps = {
  flow: FIELD_FLOW_VERTICAL,
  isDisabled: false
};
export default Field;